import { useState } from 'react';
import { ApiError, api, formatApiError } from '../../api';
import { Section } from './_ui';

// 409 from /review means the session row hasn't reached status='complete'
// yet — give a more specific hint than the generic submit failure.
// Other errors fall through to the shared envelope parser.
function formatReviewError(e: unknown): string {
  if (e instanceof ApiError && e.status === 409) {
    return formatApiError(e, '解讀尚未完成，請重新整理頁面後再試。');
  }
  return formatApiError(e, '送出失敗，請稍後再試。');
}

// STA-151 — 1-5 star review submission. Backed by
// POST /readings/sessions/{id}/review; the server upserts so re-rating
// the same session simply overwrites the previous row.
export function RatingPanel({ sessionId, mentorName }: { sessionId: string; mentorName?: string | null }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (rating < 1) return;
    setBusy(true);
    setError(null);
    try {
      await api.submitMentorReview(sessionId, {
        rating,
        review_text: text.trim() || null,
      });
      setSubmitted(true);
    } catch (e) {
      // 409 from /review means session.status != 'complete' — the FE's
      // post-stream /complete call may have failed silently (the only
      // path that gets us to the rating panel without status=complete).
      // Try to nudge the session to complete once, then re-submit.
      if (e instanceof ApiError && e.status === 409) {
        try {
          const completeRes = await fetch(`/api/v1/readings/sessions/${sessionId}/complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...api.authHeaders(),
            },
            body: JSON.stringify({}),
          });
          if (api.handleAuthFailure(completeRes.status)) return;
          await api.submitMentorReview(sessionId, {
            rating,
            review_text: text.trim() || null,
          });
          setSubmitted(true);
          return;
        } catch (e2) {
          // Fall through to surface the original 409 with a friendly hint.
          if (e2 instanceof ApiError && e2.status === 409) {
            setError(formatReviewError(e2));
            return;
          }
        }
      }
      setError(formatReviewError(e));
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <Section title="評分">
        <div className="card text-center text-tarot-muted">
          已收到您的評分，感謝 ✦
        </div>
      </Section>
    );
  }

  const active = hover || rating;
  return (
    <Section title={mentorName ? `為 ${mentorName} 評分` : '為這次解讀評分'}>
      <div className="card">
        <div
          className="flex items-center justify-center gap-2 text-3xl"
          onMouseLeave={() => setHover(0)}
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-label={`${n} 星`}
              onMouseEnter={() => setHover(n)}
              onClick={() => setRating(n)}
              className={`transition-colors ${
                n <= active ? 'text-tarot-gold' : 'text-tarot-muted/40 hover:text-tarot-mauve'
              }`}
            >
              ★
            </button>
          ))}
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="想留下感想？（選填，2000 字以內）"
          maxLength={2000}
          className="input min-h-[5rem] mt-4"
          disabled={busy}
        />
        {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
        <div className="flex justify-end mt-3">
          <button
            type="button"
            onClick={submit}
            disabled={busy || rating < 1}
            className="btn-primary"
          >
            {busy ? '送出中…' : '送出評分'}
          </button>
        </div>
      </div>
    </Section>
  );
}
